import { useEffect, useState } from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    Divider,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    IconButton,
    Tooltip,
    Autocomplete,
    Paper
} from "@mui/material";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { hasSelectedBaseCondition } from "./RuleValidation";

const ALL_STORES_OPTION_ID = "__ALL_STORES__";
const ALL_STORES_OPTION = { id: ALL_STORES_OPTION_ID, name: "All Stores" };
const ALL_CUSTOMER_GROUPS_OPTION_ID = "__ALL_CUSTOMER_GROUPS__";
const ALL_CUSTOMER_GROUPS_OPTION = {
    id: ALL_CUSTOMER_GROUPS_OPTION_ID,
    name: "All Customer Groups"
};

function ConditionRow({ row, onChange, onRemove, attributeConfig, options, isDisabled }) {
    const attributeMeta = attributeConfig.find(
        a => a.attributeType.toLowerCase() === row.attributeType?.toLowerCase()
    );

    const fields = attributeMeta?.fields || [];
    const helperText = attributeMeta?.helperText || "";

    const selectedFieldMeta = fields.find(
        f => f.name === row.attributeFilterType
    );

    const operators = selectedFieldMeta?.operators || [];

    let OPTIONS = options || [];
    const isStoreSelector = row.attributeFilterType === "storeIds";
    const isCustomerGroupSelector = row.attributeFilterType === "customerGroups";

    if (isStoreSelector) {
        OPTIONS = (options || []).map((store) => ({
            name: store.storeName,
            id: store.storeId
        }));
    }

    if (isCustomerGroupSelector) {
        OPTIONS = (options || []).map((g) => ({
            id: g.id ?? g.customerGroupId,
            name: g.name ?? g.customerGroupName
        }));
    }

    const clause = row.attributeFilterClause?.trim();
    const isMulti = clause === "IN";
    const normalizedOptions = Array.isArray(OPTIONS) ? OPTIONS : [];

    const value =
        clause === "EQUALS"
            ? row.attributeFilterValue ?? ""
            : isMulti
                ? Array.isArray(row.attributeFilterValue)
                    ? row.attributeFilterValue
                    : []
                : row.attributeFilterValue ?? "";

    const isDisableOption = isDisabled || !row.attributeFilterType || !row.attributeFilterClause;
    const selectedKey = row.attributeFilterType;

    const getOptionId = (opt) => {
        if (!opt || typeof opt !== "object") return null;

        if (row.attributeFilterType === "storeIds") {
            return opt.id ?? opt.storeId ?? null;
        }

        if (row.attributeType?.toUpperCase() === "SKU") {
            return opt.productItemId ?? null;
        }

        if (row.attributeType?.toUpperCase() === "PRODUCT") {
            return opt.id ?? null;
        }

        return opt.id ?? opt[selectedKey] ?? null;
    };

    const getOptionName = (opt) => {
        if (!opt || typeof opt !== "object") return "";
        return opt.name ?? opt.storeName ?? "";
    };

    const toLabel = (opt) => {
        if (!opt) return "";
        if (typeof opt === "string" || typeof opt === "number") return String(opt);

        if (
            (isStoreSelector && opt.id === ALL_STORES_OPTION_ID) ||
            (isCustomerGroupSelector && opt.id === ALL_CUSTOMER_GROUPS_OPTION_ID)
        ) {
            return opt.name || "";
        }

        const id = getOptionId(opt);
        const name = getOptionName(opt);

        if (opt?.[selectedKey] != null) {
            return `${opt[selectedKey]}${opt?.name ? ` - ${opt.name}` : ""}`;
        }


        if (id != null && name) return `${id} - ${name}`;
        if (id != null) return String(id);
        if (name) return name;

    };

    const resolveSingleValue = (raw) => {
        if (raw == null || raw === "") return null;
        if (typeof raw === "object") return raw;

        const match = normalizedOptions.find((opt) => {
            if (!opt || typeof opt !== "object") return false;

            const id = getOptionId(opt);
            const keyed = opt?.[selectedKey];
            const name = getOptionName(opt);

            return (
                (id != null && String(id) === String(raw)) ||
                (keyed != null && String(keyed) === String(raw)) ||
                (name && String(name) === String(raw))
            );
        });

        return match || raw;
    };

    const resolvedValue = isMulti
        ? (Array.isArray(value) ? value.map(resolveSingleValue).filter((v) => v != null) : [])
        : resolveSingleValue(value);

    const autocompleteOptions =
        (isStoreSelector || isCustomerGroupSelector) && isMulti
            ? [
                ...(isStoreSelector ? [ALL_STORES_OPTION] : []),
                ...(isCustomerGroupSelector ? [ALL_CUSTOMER_GROUPS_OPTION] : []),
                ...normalizedOptions
            ]
            : normalizedOptions;

    const hasKeyInOptions =
        selectedKey &&
        normalizedOptions.length > 0 &&
        typeof normalizedOptions[0] === "object" &&
        selectedKey in normalizedOptions[0] &&
        normalizedOptions[0][selectedKey] != null;

    const selectedValueCount = isMulti && Array.isArray(value)
        ? value.filter((v) => v !== null && v !== undefined && `${v}`.trim() !== "").length
        : 0;

    const showInError = isMulti && selectedValueCount > 0 && selectedValueCount < 2 && operators.length > 1;
    const noOptionsAvailable = options.length === 0 && row.attributeFilterType && row.attributeFilterClause;
    const useAuto = (Array.isArray(options) && options.length > 0) || hasKeyInOptions || isMulti;


    return (
        <Grid
            container
            spacing={2}
            sx={{
                mb: 3,
                width: "100%",
                alignItems: "center",
                flexWrap: "nowrap"
            }}
        >
            <Grid item sx={{ flex: "0 0 18%" }}>
                <TextField
                    label="Attribute Type"
                    value={attributeMeta.label || row.attributeType || ""}
                    fullWidth
                    size="small"
                    slotProps={{
                        readOnly: true
                    }}
                />
            </Grid>

            <Grid item sx={{ flex: "0 0 22%" }}>
                <FormControl fullWidth size="small">
                    {row.attributeType === "CONTEXT" && ['storeIds', 'customerGroups'].includes(row.attributeFilterType) || isDisabled ? (
                        <TextField
                            label="Attribute"
                            value={row.attributeFilterType || ""}
                            size="small"
                            fullWidth
                            slotProps={{ readOnly: true }}
                        />
                    ) : (
                        <>
                            <InputLabel>Attribute</InputLabel>
                            <Select
                                label="Attribute"
                                value={row.attributeFilterType?.trim() || ""}
                                onChange={(e) => onChange("attributeFilterType", e.target.value)}
                            >
                                {fields.filter((f) => !['storeIds', 'customerGroups'].includes(f.name)).map((f) => (
                                    <MenuItem key={f.name} value={f.name}>
                                        {f.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </>
                    )}
                </FormControl>
            </Grid>

            <Grid item sx={{ flex: "0 0 14%" }}>
                <FormControl fullWidth size="small">
                    <InputLabel>Operator</InputLabel>
                    <Select
                        label="Operator"
                        value={row.attributeFilterClause?.trim() || ""}
                        onChange={(e) => onChange("attributeFilterClause", e.target.value)}
                        disabled={isDisabled}
                    >
                        {operators.map((op) => (
                            <MenuItem key={op} value={op}>
                                {op}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>

            <Grid item sx={{ flex: "1 1 auto", minWidth: 0 }}>
                <FormControl fullWidth size="small">
                    {useAuto ? (
                        <Autocomplete
                            sx={{ width: "100%" }}
                            multiple={isMulti}
                            limitTags={1}
                            disableCloseOnSelect
                            options={autocompleteOptions}
                            value={isMulti ? resolvedValue : (resolvedValue || null)}
                            getOptionLabel={toLabel}
                            isOptionEqualToValue={(option, val) => {
                                if (!option || !val) return false;

                                if (typeof option === "object" && typeof val === "object") {
                                    const optionId = getOptionId(option);
                                    const valueId = getOptionId(val);
                                    if (optionId != null && valueId != null) {
                                        return String(optionId) === String(valueId);
                                    }
                                }

                                return String(toLabel(option)) === String(toLabel(val));
                            }}
                            onChange={(_, newValue, __, details) => {
                                if ((isStoreSelector || isCustomerGroupSelector) && isMulti) {

                                    const isAllOptionClicked =
                                        details?.option?.id === ALL_STORES_OPTION_ID ||
                                        details?.option?.id === ALL_CUSTOMER_GROUPS_OPTION_ID;

                                    if (isAllOptionClicked) {
                                        const allSelected =
                                            Array.isArray(value) &&
                                            normalizedOptions.length > 0 &&
                                            normalizedOptions.every((opt) =>
                                                value.some(v => String(getOptionId(v)) === String(getOptionId(opt)))
                                            );

                                        onChange(
                                            "attributeFilterValue",
                                            allSelected ? [] : normalizedOptions
                                        );
                                        return;
                                    }

                                    onChange(
                                        "attributeFilterValue",
                                        Array.isArray(newValue)
                                            ? newValue.filter(
                                                (option) =>
                                                    option?.id !== ALL_STORES_OPTION_ID &&
                                                    option?.id !== ALL_CUSTOMER_GROUPS_OPTION_ID
                                            )
                                            : []
                                    );

                                    return;
                                }

                                onChange("attributeFilterValue", newValue);
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Select Value"
                                    size="small"
                                    error={showInError}
                                    helperText={showInError ? "Select at least 2 values for IN operator" : ""}
                                    sx={{
                                        "& .MuiAutocomplete-inputRoot": {
                                            flexWrap: "nowrap",
                                            overflow: "hidden"
                                        }
                                    }}
                                />
                            )}
                            disabled={isDisableOption}
                        />
                    ) : (
                        <TextField
                            size="small"
                            fullWidth
                            value={row.attributeFilterValue || ""}
                            onChange={(e) =>
                                onChange("attributeFilterValue", e.target.value)
                            }
                            placeholder="Enter value…"
                            disabled={isDisableOption}
                            error={noOptionsAvailable}
                            helperText={helperText || (noOptionsAvailable && "No options available")}
                        />
                    )}
                </FormControl>
            </Grid>

            <Grid
                item
                sx={{
                    flex: "0 0 50px",
                    display: "flex",
                    justifyContent: "center"
                }}
            >
                <Tooltip title="Clear">
                    <IconButton
                        onClick={onRemove}
                        sx={{
                            border: "1px solid #ff4d4f",
                            color: "#ff4d4f",
                            borderRadius: 2,
                            width: 38,
                            height: 38,
                        }}
                    >
                        <CancelOutlinedIcon />
                    </IconButton>
                </Tooltip>
            </Grid>
        </Grid>
    );
}

export default function RuleAssociations({ form, setField, errors,
    catalogs, locations, categories, products, skus,
    setSelectedCategory, setSelectedProduct, setSelectedSku, attributeConfig, enums }) {

    // Main conditions list

    const buildBaseConditions = () => {
        if (!attributeConfig?.length) return [];

        const base = [];

        attributeConfig.forEach((a) => {
            // Non-context rows
            if (a.attributeType !== "CONTEXT" && a.attributeType !== "SESSION") {
                base.push({
                    id: a.attributeType.toUpperCase(),
                    attributeType: a.attributeType.toUpperCase(),
                    attributeFilterType: "",
                    attributeFilterClause: "",
                    attributeFilterValue: "",
                });
            }

            // Context rows in base WHEN block
            if (a.attributeType === "CONTEXT" || a.attributeType === "SESSION") {
                const fields = (a.fields || []).filter((f) =>
                    ["customerGroups", "storeIds"].includes(f.name)
                );

                fields.forEach((f) => {
                    base.push({
                        id: f.name,
                        attributeType: "CONTEXT",
                        attributeFilterType: f.name,
                        attributeFilterClause: "",
                        attributeFilterValue: ""
                    });
                });
                base.push({
                    id: a.attributeType.toUpperCase(),
                    attributeType: "CONTEXT",
                    attributeFilterType: "",
                    attributeFilterClause: "",
                    attributeFilterValue: ""
                });
            }
        });

        return base;
    };

    const mergeFormWhenIntoBase = (base, whenRows) => {
        const when = Array.isArray(whenRows) ? whenRows : [];

        return base.map((baseItem) => {
            const isContext = baseItem.attributeType === "CONTEXT";

            // IMPORTANT: for CONTEXT rows, match by attributeFilterType only
            const match = when.find((w) => {
                if (isContext) {
                    return (
                        w.attributeType === "CONTEXT" &&
                        w.attributeFilterType === baseItem.attributeFilterType
                    );
                }
                return w.attributeType === baseItem.attributeType;
            });

            return {
                ...baseItem,
                attributeFilterType: match?.attributeFilterType ?? baseItem.attributeFilterType,
                attributeFilterClause: match?.attributeFilterClause ?? baseItem.attributeFilterClause,
                attributeFilterValue: match?.attributeFilterValue ?? baseItem.attributeFilterValue,
            };
        });
    };

    const [conditions, setConditions] = useState(() => {
        const base = buildBaseConditions();
        return mergeFormWhenIntoBase(base, form?.when);
    });

    useEffect(() => {
        if (!form?.id) return;

        const base = buildBaseConditions();
        const merged = mergeFormWhenIntoBase(base, form?.when);

        const current = JSON.stringify(conditions);
        const next = JSON.stringify(merged);

        if (current !== next) {
            setConditions(merged);
        }
    }, [form?.id, locations?.stores, attributeConfig]);

    const handleUpdateContext = (id, key, value) => {
        setField(id, value);
    };


    const resetThenConditionsByTypes = (typesToReset) => {
        if (!typesToReset || typesToReset.size === 0) return;

        const currentThen = form?.then || {};
        const currentSubConditions = Array.isArray(currentThen.subConditions)
            ? currentThen.subConditions
            : [];

        const nextSubConditions = currentSubConditions.map((block) => ({
            ...block,
            when: (block.when || []).map((cond) => {
                const condType = String(cond?.attributeType || "").toUpperCase();
                const contextField = String(cond?.attributeFilterType || "").toUpperCase();
                const isBaseTypeMatch = typesToReset.has(condType);
                const isContextFieldMatch =
                    condType === "CONTEXT" && typesToReset.has(contextField);

                if (!(isBaseTypeMatch || isContextFieldMatch)) return cond;

                return {
                    ...cond,
                    attributeFilterClause: "",
                    attributeFilterValue: "",
                };
            }),
        }));

        setField("then", {
            ...currentThen,
            subConditions: nextSubConditions,
        });
    };

    const handleUpdateCondition = (id, key, value) => {
        const activeRow = conditions.find((row) => row.id === id);
        const activeType = activeRow?.attributeType?.toUpperCase();
        const isValueUpdate = key === "attributeFilterValue";
        const isClauseUpdate = key === "attributeFilterClause";

        const typesToResetInThen = new Set();

        // same/dependent types to reset in Outcome (form.then.subConditions[*].when)
        if (isValueUpdate && activeType === "CATEGORY") {
            typesToResetInThen.add("CATEGORY");
            typesToResetInThen.add("PRODUCT");
            typesToResetInThen.add("SKU");
        } else if (isValueUpdate && activeType === "PRODUCT") {
            typesToResetInThen.add("PRODUCT");
            typesToResetInThen.add("SKU");
        } else if (isValueUpdate && activeType === "SKU") {
            typesToResetInThen.add("SKU");
        } else if (isValueUpdate && activeType === "CONTEXT" && activeRow.attributeFilterType === "customerGroups") {
            typesToResetInThen.add("CUSTOMERGROUPS");
        }
        else if (isValueUpdate && activeType === "CONTEXT" && activeRow.attributeFilterType === "storeIds") {
            typesToResetInThen.add("STOREIDS");
        } else if (isValueUpdate && activeType === "CONTEXT" && activeRow.attributeFilterType === "geotagIds") {
            typesToResetInThen.add("GEOTAGIDS");
        }

        // Keep selected helpers in sync
        if (isValueUpdate) {
            if (activeType === "CATEGORY") {
                setSelectedCategory(value);
                setSelectedProduct(null);
                setSelectedSku(null);
            } else if (activeType === "PRODUCT") {
                setSelectedProduct(value);
                setSelectedSku(null);
            } else if (activeType === "SKU") {
                setSelectedSku(value);
            }
        }

        setConditions((prev) => {
            const next = prev.map((row) => {
                if (row.id === id) {
                    return {
                        ...row,
                        attributeFilterValue: isClauseUpdate ? "" : row.attributeFilterValue,
                        [key]: value
                    };
                }

                if (
                    isValueUpdate &&
                    activeType === "CATEGORY" &&
                    ["PRODUCT", "SKU"].includes(row.attributeType?.toUpperCase())
                ) {
                    return {
                        ...row,
                        attributeFilterClause: "",
                        attributeFilterValue: "",
                    };
                }

                if (
                    isValueUpdate &&
                    activeType === "PRODUCT" &&
                    row.attributeType?.toUpperCase() === "SKU"
                ) {
                    return {
                        ...row,
                        attributeFilterClause: "",
                        attributeFilterValue: "",
                    };
                }

                return row;
            });

            setField("when", next);
            return next;
        });
        // Also reset matching Outcome conditions in form.then
        resetThenConditionsByTypes(typesToResetInThen);
    };

    const removeContext = (id) => {
        if (id === "storeIds") {
            setField("storeIds", []);
        }

        if (id === "geoTagIds") {
            setField("geoTagIds", []);
        }
    };

    const removeCondition = (id) => {
        const activeRow = conditions.find((row) => row.id === id);
        const activeType = activeRow?.attributeType?.toUpperCase();
        if (activeType === "CATEGORY") {
            setSelectedCategory(null);
        } else if (activeType === "PRODUCT") {
            setSelectedProduct(null);
        } else if (activeType === "SKU") {
            setSelectedSku(null);
        }

        setConditions((prev) => {
            const next = prev.map((r) => {
                if (r.id == id) {
                    r.attributeFilterType = r.attributeType != "CONTEXT" ? "" : r.attributeFilterType;
                    r.attributeFilterClause = "";
                    r.attributeFilterValue = "";
                }
                return r;
            });
            setField("when", next);
            return next;
        });
    };

    const handleRowUpdate = (row, key, value) => {
        if (row.attributeType === "CONTEXT" && (row.attributeFilterType === "storeIds" || row.attributeFilterType === "geoTagIds") && key === 'attributeFilterValue') {
            handleUpdateContext(row.id, key, value);
        }
        handleUpdateCondition(row.id, key, value);

    };

    const handleRowRemove = (row) => {
        if (row.attributeType === "CONTEXT" && (row.attributeFilterType === "storeIds" || row.attributeFilterType === "geoTagIds")) {
            removeContext(row.id);
        }
        removeCondition(row.id);

    };

    const allRows = [...conditions];

    const isSelected = (row) =>
        row.attributeFilterClause &&
        (Array.isArray(row.attributeFilterValue)
            ? row.attributeFilterValue.length > 0
            : row.attributeFilterValue !== "" && row.attributeFilterValue != null);

    const selectedRows = allRows.filter(isSelected);
    const unselectedRows = allRows.filter((r) => !isSelected(r));

    const getOptions = (row) => {
        if (row.attributeFilterType === "storeIds") return locations?.stores || [];
        if (row.attributeFilterType === "geoTagIds") return locations?.geoTags || [];

        const type = row.attributeType?.toUpperCase();

        if (type === "CATEGORY") return categories || [];
        if (type === "PRODUCT") return products || [];
        if (type === "SKU") return skus || [];

        if (row.attributeFilterType === "customerGroups") {
            return locations?.customerGroups || [];
        }
        if (row.attributeFilterType === "customerType") return enums.CustomerType || [];
        if (row.attributeFilterType === "appSource") return locations?.requestSources || [];

        return [];
    };

    const onCatalogChange = (catalogId) => {
        if (!form.id) {
            setField("catalogId", catalogId);
            const base = buildBaseConditions();
            setConditions(mergeFormWhenIntoBase(base, []));
        }
    }

    return (
        <Card sx={{ borderRadius: 3, mb: 3, width: "100%", maxWidth: "100%" }}>
            <CardContent>
                <Typography sx={{ fontWeight: 800, mb: 2 }}>
                    Step 2: <span style={{ fontWeight: 700 }}>Rule Associations</span>
                </Typography>

                {/* Catalog Card */}
                <Paper
                    variant="outlined"
                    sx={{
                        borderRadius: 2,
                        p: 2,
                        mb: 2,
                        width: "100%"
                    }}
                >
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid size={{ xs: 12 }}>
                            <FormControl fullWidth size="small" error={!!errors?.catalogId}>
                                <InputLabel>Select Catalog</InputLabel>

                                <Select
                                    label="Select Catalog"
                                    value={form.catalogId || ""}
                                    onChange={(e) => onCatalogChange(e.target.value)}
                                    fullWidth
                                >
                                    {catalogs.map((c) => (
                                        <MenuItem key={c.catalogId} value={c.catalogId}>
                                            {c.catalogName}
                                        </MenuItem>
                                    ))}
                                </Select>

                                {errors?.catalogId && (
                                    <Typography sx={{ fontSize: 12, color: "error.main", mt: 0.5 }}>
                                        {errors.catalogId}
                                    </Typography>
                                )}
                            </FormControl>
                        </Grid>
                    </Grid>
                </Paper>

                {/* Match All */}

                {selectedRows.length > 0 && <Paper
                    variant="outlined"
                    sx={{
                        borderRadius: 2,
                        p: 2,
                        mb: 2,
                        width: "100%"
                    }}
                >
                    <Typography sx={{ mb: 3 }}>
                        Match All
                    </Typography>

                    {/* Selected Rows */}
                    {selectedRows.map((row) => (
                        <ConditionRow
                            key={row.id}
                            row={row}
                            onChange={(key, value) => handleRowUpdate(row, key, value)}
                            onRemove={() => handleRowRemove(row)}
                            options={getOptions(row)}
                            isDisabled={!form.catalogId}
                            attributeConfig={attributeConfig}
                        />
                    ))}
                </Paper>}
                {unselectedRows.length > 0 && <Paper
                    variant="outlined"
                    sx={{
                        borderRadius: 2,
                        p: 2,
                        mb: 2,
                        width: "100%",
                        paddingTop: 4
                    }}
                >

                    {/* Unselected Rows */}
                    {unselectedRows.map((row) => (
                        <ConditionRow
                            key={row.id}
                            row={row}
                            onChange={(key, value) => handleRowUpdate(row, key, value)}
                            onRemove={() => handleRowRemove(row)}
                            options={getOptions(row)}
                            isDisabled={!form.catalogId}
                            attributeConfig={attributeConfig}
                        />
                    ))}

                    {!hasSelectedBaseCondition(form.when) && (
                        <Typography sx={{ fontSize: 12, color: "warning.main", mb: 2 }}>
                            Please select at least one of these conditions: Category, Product, SKU, or Customer Group.
                        </Typography>
                    )}
                </Paper>
                }
            </CardContent>
        </Card>
    );
}